/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * @file QuestionTopicsModal.jsx
 * @author Anuja Gaikwad
 * @since 2.0.3
 */

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  QuestionTopicsDoneButton,
  QuestionTopicsModalStylesRoot,
  classes
} from './styles/QuestionTopicsModalStyles';
import AssignmentQuestionCard from './AssignmentQuestionCard';

const viewPropType = PropTypes.shape({
  title: PropTypes.string.isRequired,
  dueDateLabel: PropTypes.string.isRequired,
  dueDateFormatted: PropTypes.string,
  questionsTotalLabel: PropTypes.string.isRequired,
  totalQuestions: PropTypes.number.isRequired,
  chapters: PropTypes.array.isRequired,
  selectedPageId: PropTypes.string,
  questions: PropTypes.array.isRequired,
  textInfo: PropTypes.object.isRequired
});

const MetaChip = ({ label, value }) => (
  <div className={classes.metaChip}>
    {label}
    <span className={classes.metaChipValue}>{value}</span>
  </div>
);

MetaChip.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

const QuestionTopicsModal = ({
  open,
  view,
  onClose,
  onDone,
  onTopicSelect
}) => {
  if (!open || !view) {
    return null;
  }

  const {
    title,
    dueDateLabel,
    dueDateFormatted,
    questionsTotalLabel,
    totalQuestions,
    chapters,
    questions,
    textInfo,
    selectedPageId
  } = view;

  const emptyMessage = !selectedPageId
    ? textInfo.noSectionSelectedMessage
    : textInfo.noQuestionsMessage;

  return (
    <QuestionTopicsModalStylesRoot
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      <IconButton
        className={classes.closeButton}
        onClick={onClose}
        aria-label={textInfo.closeLabel}
      >
        <CloseIcon />
      </IconButton>

      <div className={classes.root}>
        <header className={classes.header}>
          <Typography component="h2" className={classes.assignmentTitle}>
            {title}
          </Typography>
          <div className={classes.metaRow}>
            <MetaChip label={dueDateLabel} value={dueDateFormatted || ''} />
            <MetaChip label={questionsTotalLabel} value={totalQuestions} />
          </div>
        </header>

        <div className={classes.bodyRow}>
          <nav className={classes.sectionNav} aria-label={textInfo.sectionsNavLabel}>
            <div className={classes.sectionNavScroll}>
              <div className={classes.sectionNavContent}>
                {chapters.map((chapter, index) => (
                  <div
                    key={chapter.chapterId || chapter.chapterTitle}
                    className={classes.chapterGroup}
                  >
                    <Typography component="h3" className={classes.chapterTitle}>
                      {chapter.chapterTitle}
                    </Typography>
                    <ul className={classes.topicList}>
                      {(chapter.topics || []).map((topic) => {
                        const isSelected = selectedPageId === topic.pageId;

                        return (
                          <li key={topic.pageId}>
                            <button
                              type="button"
                              className={[
                                classes.topicItem,
                                isSelected && classes.topicItemSelected,
                                topic.questionCount === 0 && classes.topicItemDisabled
                              ].filter(Boolean).join(' ')}
                              onClick={() => onTopicSelect(topic.pageId)}
                            >
                              {topic.label}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    {index < chapters.length - 1 && <hr className={classes.chapterDivider} />}
                  </div>
                ))}
              </div>
            </div>
          </nav>

          <section
            key={selectedPageId}
            className={classes.questionsPane}
            aria-label={textInfo.questionsPanelLabel}
          >
            <div className={classes.questionsPaneContent}>
              <Typography component="p" className={classes.questionsLabel}>
                {textInfo.questionsLabel}
              </Typography>
              {!selectedPageId || !questions.length ? (
                <Typography component="p" className={classes.emptyQuestions}>
                  {emptyMessage}
                </Typography>
              ) : (
                <div className={classes.questionsList}>
                  {questions.map((question) => (
                    <AssignmentQuestionCard
                      key={question.questionId}
                      questionLabel={question.label}
                      questionStem={question.stem}
                      options={question.options}
                      defaultExpanded={question.defaultExpanded}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        <footer className={classes.footer}>
          <QuestionTopicsDoneButton type="button" onClick={onDone}>
            {textInfo.doneLabel}
          </QuestionTopicsDoneButton>
        </footer>
      </div>
    </QuestionTopicsModalStylesRoot>
  );
};

QuestionTopicsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  view: viewPropType,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  onTopicSelect: PropTypes.func.isRequired
};

QuestionTopicsModal.defaultProps = {
  view: null
};

export default memo(QuestionTopicsModal);
